import { useLayoutEffect, useRef, useState } from "react";

type NotchType = {
  position: { x: number; y: number };
  radius: number;
};

type NamedPosition = {
  posX: "Left" | "Right" | "Center";
  posY: "Top" | "Bottom" | "Center";
  radius: number;
};

type Edge = "Top" | "Bottom" | "Left" | "Right";

function resolvePosition({ posX, posY, radius, w, h }: NamedPosition & { w: number; h: number }): NotchType {
  const x = posX === "Left" ? 0 : posX === "Right" ? w : w / 2;
  const y = posY === "Top" ? 0 : posY === "Bottom" ? h : h / 2;
  return { position: { x, y }, radius };
}

function getEdge(x: number, y: number, w: number, h: number): Edge {
  if (y === 0) return "Top";
  if (y >= h) return "Bottom";
  if (x === 0) return "Left";
  return "Right";
}

function buildPath(notches: NotchType[], w: number, h: number, borderRadius = 0): string {
  if (w === 0 || h === 0) return "";

  type NotchOnEdge = { t: number; radius: number; edge: Edge };
  const corners = { tl: 0, tr: 0, br: 0, bl: 0 };
  const edgeNotches: NotchOnEdge[] = [];
  const R = borderRadius;

  for (const { position: { x, y }, radius } of notches) {
    if      (x === 0   && y === 0 ) corners.tl = radius;
    else if (x >= w    && y === 0 ) corners.tr = radius;
    else if (x >= w    && y >= h  ) corners.br = radius;
    else if (x === 0   && y >= h  ) corners.bl = radius;
    else {
      const edge = getEdge(x, y, w, h);
      const t = edge === "Top" || edge === "Bottom" ? x : y;
      edgeNotches.push({ t, radius, edge });
    }
  }

  const { tl, tr, br, bl } = corners;
  const top    = edgeNotches.filter(n => n.edge === "Top")   .sort((a, b) => a.t - b.t);
  const right  = edgeNotches.filter(n => n.edge === "Right") .sort((a, b) => a.t - b.t);
  const bottom = edgeNotches.filter(n => n.edge === "Bottom").sort((a, b) => b.t - a.t);
  const left   = edgeNotches.filter(n => n.edge === "Left")  .sort((a, b) => b.t - a.t);
  const tlR = tl || R, trR = tr || R, brR = br || R, blR = bl || R;
  const tlS = tl ? 0 : 1, trS = tr ? 0 : 1, brS = br ? 0 : 1, blS = bl ? 0 : 1;

  const p: string[] = [`M ${tlR} 0`];

  for (const n of top) {
    p.push(`L ${n.t - n.radius} 0`);
    p.push(`A ${n.radius} ${n.radius} 0 0 0 ${n.t + n.radius} 0`);
  }
  if (trR > 0) { p.push(`L ${w - trR} 0`); p.push(`A ${trR} ${trR} 0 0 ${trS} ${w} ${trR}`); }
  else p.push(`L ${w} 0`);

  for (const n of right) {
    p.push(`L ${w} ${n.t - n.radius}`);
    p.push(`A ${n.radius} ${n.radius} 0 0 0 ${w} ${n.t + n.radius}`);
  }
  if (brR > 0) { p.push(`L ${w} ${h - brR}`); p.push(`A ${brR} ${brR} 0 0 ${brS} ${w - brR} ${h}`); }
  else p.push(`L ${w} ${h}`);

  for (const n of bottom) {
    p.push(`L ${n.t + n.radius} ${h}`);
    p.push(`A ${n.radius} ${n.radius} 0 0 0 ${n.t - n.radius} ${h}`);
  }
  if (blR > 0) { p.push(`L ${blR} ${h}`); p.push(`A ${blR} ${blR} 0 0 ${blS} 0 ${h - blR}`); }
  else p.push(`L 0 ${h}`);

  for (const n of left) {
    p.push(`L 0 ${n.t + n.radius}`);
    p.push(`A ${n.radius} ${n.radius} 0 0 0 0 ${n.t - n.radius}`);
  }
  if (tlR > 0) { p.push(`L 0 ${tlR}`); p.push(`A ${tlR} ${tlR} 0 0 ${tlS} ${tlR} 0`); }

  p.push("Z");
  return p.join(" ");
}

type BorderStyle = "solid" | "dashed" | "dotted" | "none";

type LineConfig = {
  from: { x: number; y: number };
  to: { x: number; y: number };
  color?: string;
  width?: number;
  style?: "solid" | "dashed" | "dotted";
};

const DASH_PATTERNS: Record<BorderStyle, number[]> = {
  solid:  [],
  dashed: [10, 4],
  dotted: [2, 2],
  none:   [],
};

function renderToDataUrl(
  rawW: number,
  rawH: number,
  fillColor: string,
  borderColor?: string,
  borderStyle: BorderStyle = "solid",
  strokeWidth = 1,
  notches: NotchType[] = [],
  positions: NamedPosition[] = [],
  borderRadius = 0,
  lines: LineConfig[] = [],
): string {
  const dpr = window.devicePixelRatio ?? 1;
  // Round to nearest physical pixel so x- and y-scale are both exactly 1.0
  const physW = Math.round(rawW * dpr);
  const physH = Math.round(rawH * dpr);
  // CSS-space dimensions that exactly fit the canvas (avoids non-uniform stretch)
  const w = physW / dpr;
  const h = physH / dpr;

  const resolvedNotches = positions.map(p => resolvePosition({ ...p, w, h }));
  const allNotches = [...notches, ...resolvedNotches];
  const pathStr = buildPath(allNotches, w, h, borderRadius);
  if (!pathStr) return "";

  const canvas = document.createElement("canvas");
  canvas.width = physW;
  canvas.height = physH;
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  const path = new Path2D(pathStr);
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = fillColor;
  ctx.fill(path);
  if (lines.length > 0) {
    ctx.save();
    ctx.clip(path);
    for (const line of lines) {
      ctx.beginPath();
      ctx.strokeStyle = line.color ?? "#000000";
      ctx.lineWidth = line.width ?? 1;
      ctx.setLineDash(DASH_PATTERNS[line.style ?? "solid"]);
      ctx.moveTo(line.from.x, line.from.y);
      ctx.lineTo(line.to.x, line.to.y);
      ctx.stroke();
    }
    ctx.restore();
  }
  if (borderColor && borderStyle !== "none") {
    ctx.save();
    ctx.clip(path);
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = strokeWidth * 2;
    ctx.setLineDash(DASH_PATTERNS[borderStyle]);
    ctx.stroke(path);
    ctx.restore();
  }
  return canvas.toDataURL("image/png");
}

export type { LineConfig };

export type LinesFn = (w: number, h: number) => LineConfig[];

// canvas can't read CSS variables, so "var(--x)" is resolved to its current value when drawing
function resolveColor(color: string) {
  const name = color.match(/^var\(\s*(--[^,)\s]+)/)?.[1];
  if (!name) return color;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || color;
}

// bumps whenever the root element's theme tokens change, so the shape gets redrawn
function useThemeVersion() {
  const [version, setVersion] = useState(0);

  useLayoutEffect(() => {
    const observer = new MutationObserver(() => setVersion((v) => v + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class", "data-theme"] });
    return () => observer.disconnect();
  }, []);

  return version;
}

export default function useGetVoucherCardShape({
  notches = [],
  positions = [],
  fillColor = "transparent",
  borderColor,
  borderStyle = "solid",
  strokeWidth = 1,
  borderRadius = 0,
  lines = [],
}: {
  notches?: NotchType[];
  positions?: NamedPosition[];
  fillColor?: string;
  borderColor?: string;
  borderStyle?: BorderStyle;
  strokeWidth?: number;
  borderRadius?: number;
  lines?: LineConfig[] | LinesFn;
} = {}) {
  const ref = useRef<HTMLElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const themeVersion = useThemeVersion();

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;

    const observer = new ResizeObserver(([entry]) => {
      const { inlineSize: rawW, blockSize: rawH } = entry.borderBoxSize[0];
      const dpr = window.devicePixelRatio ?? 1;
      const w = Math.round(rawW * dpr) / dpr;
      const h = Math.round(rawH * dpr) / dpr;
      const resolvedLines = typeof lines === "function" ? lines(w, h) : lines;
      setSrc(
        renderToDataUrl(rawW, rawH, resolveColor(fillColor), borderColor, borderStyle, strokeWidth, notches, positions, borderRadius, resolvedLines),
      );
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [notches, positions, fillColor, borderColor, borderStyle, strokeWidth, borderRadius, lines, themeVersion]);

  return { ref, src };
}
